'use client';

import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import { ChevronDown } from '@/lib/icons';
import { MegaMenuDropdown } from './MegaMenuDropdown';
import { MegaMenuWrapper } from './MegaMenuWrapper';
import { useSmartMenuPosition } from '@/hooks/useSmartMenuPosition';

interface NavigationItem {
  label: string;
  href: string;
  type?: 'mega';
  dropdown?: {
    label: string;
    href: string;
    description: string;
  }[];
  columns?: any[];
  cta?: any;
}

interface NavigationMenuProps {
  items: NavigationItem[];
  containerRef?: React.RefObject<HTMLDivElement>;
}

export function NavigationMenu({ items, containerRef }: NavigationMenuProps) {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navRef = useRef<HTMLElement>(null);
  
  // Smart positioning for mega menus
  const { buttonRef: megaButtonRef, position: megaPosition } = useSmartMenuPosition(
    !!openDropdown && items.find(item => item.label === openDropdown)?.type === 'mega',
    900
  );
  
  // Smart positioning for simple dropdowns
  const { buttonRef: simpleButtonRef, position: simplePosition } = useSmartMenuPosition(
    !!openDropdown && items.find(item => item.label === openDropdown)?.type !== 'mega',
    320
  );

  const handleMouseEnter = (label: string) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setOpenDropdown(label);
    setHoveredItem(label);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only trigger if actually leaving the nav item, not moving between children
    const relatedTarget = e.relatedTarget;
    // Check if relatedTarget exists and is a valid Node
    if (relatedTarget && relatedTarget instanceof Node) {
      if (!e.currentTarget.contains(relatedTarget)) {
        timeoutRef.current = setTimeout(() => {
          setOpenDropdown(null);
          setHoveredItem(null);
        }, 150);
      }
    } else {
      // If no valid relatedTarget, close the dropdown
      timeoutRef.current = setTimeout(() => {
        setOpenDropdown(null);
        setHoveredItem(null);
      }, 150);
    }
  };

  const handleMegaMenuMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleMegaMenuMouseLeave = (e: React.MouseEvent) => {
    const relatedTarget = e.relatedTarget as HTMLElement;
    
    // Don't close if moving to a nav item
    if (relatedTarget && relatedTarget instanceof HTMLElement) {
      const className = relatedTarget.className;
      if (typeof className === 'string' && className.includes('h-16 flex items-center')) {
        return;
      }
    }
    
    // Close for ALL other cases including null (browser UI/outside)
    timeoutRef.current = setTimeout(() => {
      setOpenDropdown(null);
      setHoveredItem(null);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Exit intent detection - closes menu when cursor leaves to browser UI
  useEffect(() => {
    const handleDocumentMouseLeave = (e: MouseEvent) => {
      // Only close if menu is open AND cursor is leaving upward
      if (openDropdown && e.clientY <= 0) {
        setOpenDropdown(null);
        setHoveredItem(null);
      }
    };

    document.addEventListener('mouseleave', handleDocumentMouseLeave);
    return () => document.removeEventListener('mouseleave', handleDocumentMouseLeave);
  }, [openDropdown]);

  return (
    <>
      {/* EXISTING INTERACTIVE NAVIGATION - ZERO CHANGES */}
      <nav ref={navRef} className="hidden xl:flex h-full space-x-3 min-[1440px]:space-x-6 min-[1600px]:space-x-8" aria-label="Main navigation">
        {items.map((item) => (
          <div
            key={item.label}
            ref={item.type === 'mega' ? megaButtonRef : (item.dropdown ? simpleButtonRef : null)}
            className="relative h-16 flex items-center"
            onMouseEnter={() => (item.dropdown || item.type === 'mega') && handleMouseEnter(item.label)}
            onMouseLeave={handleMouseLeave}
          >
            {item.dropdown || item.type === 'mega' ? (
              <>
                <Link
                  href={item.href}
                  className={`flex items-center space-x-1 transition-colors no-underline ${
                    hoveredItem === item.label ? 'text-primary' : 'text-neutral-700'
                  }`}
                  role="button"
                  aria-expanded={openDropdown === item.label}
                  aria-haspopup="true"
                >
                  <span className="font-medium whitespace-nowrap">{item.label}</span>
                  <ChevronDown className="h-4 w-4" />
                </Link>
              
              {openDropdown === item.label && (
                <>
                  {item.type === 'mega' && item.columns ? (
                    <MegaMenuWrapper 
                      columns={item.columns} 
                      cta={item.cta} 
                      onClose={() => setOpenDropdown(null)}
                      navRef={containerRef}
                      onMouseEnter={handleMegaMenuMouseEnter}
                      onMouseLeave={handleMegaMenuMouseLeave}
                    />
                  ) : item.dropdown ? (
                    <>
                      <noscript>
                        <style>{`
                          .simple-menu-fallback {
                            left: 50% !important;
                            transform: translateX(-50%) !important;
                            visibility: visible !important;
                          }
                        `}</style>
                      </noscript>
                      <div 
                        className="absolute top-full z-50 simple-menu-fallback"
                        style={{
                          left: simplePosition.left,
                          right: simplePosition.right,
                          transform: simplePosition.transform,
                          visibility: simplePosition.visibility
                        }}
                        onMouseEnter={handleMegaMenuMouseEnter}
                        onMouseLeave={handleMegaMenuMouseLeave}
                      >
                        <div className={`bg-white rounded-lg shadow-lg border border-gray-200 py-4 ${
                          item.dropdown.length > 8 ? 'w-[640px]' : 'w-80'
                        }`}>
                        <div className={`${
                          item.dropdown.length > 8 
                            ? 'grid grid-cols-2 gap-x-6' 
                            : 'block'
                        }`}>
                          {item.dropdown.map((dropdownItem) => (
                            <Link
                              key={dropdownItem.href}
                              href={dropdownItem.href}
                              className="block px-6 py-3 hover:bg-gray-50 transition-colors"
                            >
                              <div className="font-medium text-neutral-800 mb-1">
                                {dropdownItem.label}
                              </div>
                              <div className="text-sm text-neutral-600">
                                {dropdownItem.description}
                              </div>
                            </Link>
                          ))}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                </>
              )}
            </>
          ) : (
            <Link
              href={item.href}
              className="text-neutral-700 hover:text-primary font-medium transition-colors whitespace-nowrap"
            >
              {item.label}
            </Link>
          )}
        </div>
      ))}
      </nav>
      
      {/* SHADOW NAVIGATION: the mega-menu dropdowns are not server-rendered, so this is the only
          crawlable path from every page to ~20 money pages (verified 2026-09-28). It is
          aria-hidden, so its links must be unfocusable (tabIndex -1) — keyboard users must never
          land on links they cannot see. The only copy: MobileMenu used to render two more. */}
      <nav className="sr-only" aria-hidden="true" itemScope itemType="https://schema.org/SiteNavigationElement">
        {items.map((item) => (
          <div key={`shadow-nav-${item.label}`} itemProp="name">
            <Link 
              href={item.href} 
              itemProp="url"
              tabIndex={-1}
            >
              {item.label}
            </Link>
            {/* Render ALL mega menu column items as crawlable links */}
            {item.columns?.map((column, colIndex) => (
              <div key={`shadow-col-${colIndex}`} itemProp="name">
                {column.items?.map((subItem: any) => (
                  <Link 
                    key={`shadow-link-${subItem.href}`}
                    href={subItem.href}
                    itemProp="url"
                    tabIndex={-1}
                    title={subItem.description}
                  >
                    {subItem.label}
                  </Link>
                ))}
              </div>
            ))}
            {/* Render simple dropdown items as crawlable links */}
            {item.dropdown?.map((dropItem) => (
              <Link 
                key={`shadow-drop-${dropItem.href}`}
                href={dropItem.href}
                itemProp="url"
                tabIndex={-1}
                title={dropItem.description}
              >
                {dropItem.label}
              </Link>
            ))}
            {/* CTA links for complete crawlability */}
            {item.cta?.href && (
              <Link 
                key={`shadow-cta-${item.cta.href}`}
                href={item.cta.href}
                itemProp="url"
                tabIndex={-1}
              >
                {item.cta.buttonText || item.cta.headline}
              </Link>
            )}
          </div>
        ))}
      </nav>
    </>
  );
}